import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { installSoundPreload } from './audio/preload';
import { initI18n } from './i18n';
import { initPlatform } from './platform/analytics';
import { captureMetaIds, startMeta } from './platform/meta';
import { diag } from './platform/diag';
import { afterFirstScreen } from './platform/firstScreen';
import { installFlagFont } from './platform/flags';
import { startTracking } from './platform/tracking';
import { readSocialReturn } from './net/social';
import { registerServiceWorker } from './platform/update';
import { useApp } from './state/store';
import { CrashBoundary } from './ui/components/CrashScreen';
import './styles/global.css';

/**
 * A page from an older build asking for a script the new build no longer has: load the new
 * build instead. Deploys keep the previous builds' scripts for a week (tools/carry-assets.mjs),
 * so only an older tab gets here. At most once per 10 s, so a broken deploy can't loop.
 */
function reloadOnStaleChunk() {
  window.addEventListener('vite:preloadError', (event) => {
    try {
      const last = Number(sessionStorage.getItem('caged.staleReload') ?? 0);
      if (Date.now() - last < 10_000) return;
      sessionStorage.setItem('caged.staleReload', String(Date.now()));
    } catch {
      return;
    }
    event.preventDefault();
    location.reload();
  });
}

async function boot() {
  diag.mark('boot');
  // A link from one of our ads (D58): its click id, before anything changes the address.
  captureMetaIds();
  // Back from Google or Facebook: the sign-in sheet opens to finish it.
  const social = readSocialReturn();
  if (social) useApp.setState({ authSheet: social.mode, socialReturn: social });
  reloadOnStaleChunk();
  installFlagFont();
  installSoundPreload();
  await initI18n(useApp.getState().settings.language);
  // Tracking starts at once; its events wait until PostHog has loaded.
  startTracking();
  // The offline cache (about 2 MB to fetch on a first visit), Sentry and PostHog, and Meta's pixel
  // for our ads (D58) wait for the first screen and its hero image.
  afterFirstScreen(() => {
    registerServiceWorker();
    void initPlatform();
    startMeta();
  });
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <CrashBoundary>
        <App />
      </CrashBoundary>
    </StrictMode>,
  );
}

void boot();
